import React, { useState, useEffect } from 'react';
import { 
  Row, 
  Col, 
  Card, 
  Table, 
  Form, 
  InputGroup, 
  Button, 
  Badge, 
  Spinner,
  Modal,
  Tabs,
  Tab,
  Alert,
  Toast,
  ToastContainer
} from 'react-bootstrap';
import { 
  FaSearch, 
  FaFilter, 
  FaEye,
  FaFileInvoice,
  FaCheckCircle,
  FaTimesCircle,
  FaMoneyBillWave,
  FaExclamationTriangle,
  FaInfoCircle,
  FaDownload
} from 'react-icons/fa';
import { backofficeApi } from '../../services/api';

const BackofficeInvoices = () => {
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedInvoiceCases, setSelectedInvoiceCases] = useState([]);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);
  const [notes, setNotes] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    limit: 10
  });

  // Status mapping
  const STATUS_MAP = {
    0: { name: "Generated", variant: "info" },
    1: { name: "Processing", variant: "warning" },
    2: { name: "Disbursed", variant: "success" },
    3: { name: "Rejected", variant: "danger" }
  };

  // Payment status mapping
  const PAYMENT_STATUS_MAP = {
    0: { name: "Pending", variant: "info" },
    1: { name: "Completed", variant: "success" },
    2: { name: "Failed", variant: "danger" }
  };

  useEffect(() => {
    document.title = 'Backoffice Invoices';
    fetchInvoices();
  }, [pagination.page, statusFilter]);

  // Show toast message
  const showMessage = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  // Fetch invoices from API
  const fetchInvoices = async () => {
    setLoading(true);
    
    try {
      const status = statusFilter !== 'All' ? 
        Object.keys(STATUS_MAP).find(key => STATUS_MAP[key].name === statusFilter) : null;
      
      const response = await backofficeApi.getInvoices({
        skip: (pagination.page - 1) * pagination.limit,
        limit: pagination.limit,
        search: searchTerm || undefined,
        status: status || undefined
      });

      if (response.data && response.data.items) {
        setInvoices(response.data.items);
        setPagination(prev => ({
          ...prev,
          total: response.data.total
        }));
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      showMessage(`Failed to fetch invoices: ${error.response?.data?.detail || error.message}`, 'danger');
    } finally {
      setLoading(false);
    }
  };

  // View invoice details
  const handleViewInvoice = async (invoice) => {
    try {
      setProcessingAction(true);
      
      // Mock data for fallback
      const mockInvoiceDetails = {
        ...invoice,
        notes: invoice.notes || "Verify all documentation before processing payment."
      };
      
      // Try to fetch from API
      try {
        // Fetch detailed invoice information
        const invoiceResponse = await backofficeApi.getInvoiceDetails(invoice.id);
        
        // Fetch cases related to this invoice
        const casesResponse = await backofficeApi.getInvoiceCases(invoice.id);
        
        setSelectedInvoice(invoiceResponse.data);
        setSelectedInvoiceCases(casesResponse.data.items || []);
        setNotes(invoiceResponse.data.notes || '');
      } catch (apiError) {
        console.error('API call failed, using mock data:', apiError);
      }
      
      setShowInvoiceModal(true);
    } catch (error) {
      console.error('Error preparing invoice details:', error);
      showMessage(`Failed to load invoice details: ${error.message}`, 'danger');
    } finally {
      setProcessingAction(false);
    }
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when searching
  };

  // Handle status filter change
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when filtering
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Render status badge
  const renderStatusBadge = (statusCode) => {
    const status = STATUS_MAP[statusCode] || { name: "Unknown", variant: "secondary" };
    return <Badge bg={status.variant}>{status.name}</Badge>;
  };

  // Render payment status badge
  const renderPaymentStatusBadge = (statusCode) => {
    const status = PAYMENT_STATUS_MAP[statusCode] || { name: "Unknown", variant: "secondary" };
    return <Badge bg={status.variant}>{status.name}</Badge>;
  };

  // Handle invoice status change
  const handleStatusChange = async (invoiceId, newStatus) => {
    setProcessingAction(true);
    
    try {
      const response = await backofficeApi.updateInvoiceStatus(invoiceId, {
        status: parseInt(newStatus),
        notes: notes
      });
      
      if (response.data) {
        // Update the selected invoice with new data
        setSelectedInvoice(response.data);
        
        // Update the invoices list
        setInvoices(invoices.map(inv => 
          inv.id === invoiceId 
            ? {
                ...inv,
                status: { 
                  code: response.data.invoice_status,
                  name: STATUS_MAP[response.data.invoice_status].name
                },
                payment_status: {
                  code: response.data.payment_status,
                  name: PAYMENT_STATUS_MAP[response.data.payment_status].name
                },
                payment_date: response.data.payment_date
              } 
            : inv
        ));
        
        const statusName = STATUS_MAP[parseInt(newStatus)].name;
        showMessage(`Invoice status updated to ${statusName} successfully!`, 'success');
        setShowInvoiceModal(false);
      }
    } catch (error) {
      console.error('Error updating invoice status:', error);
      showMessage(`Failed to update invoice status: ${error.response?.data?.detail || error.message}`, 'danger');
    } finally {
      setProcessingAction(false);
      setShowInvoiceModal(false);
    }
  };

  // Calculate totals for summary cards
  const calculateTotals = () => {
    const total = invoices.reduce((sum, invoice) => sum + invoice.amount, 0);
    const requested = invoices
      .filter(inv => inv.status.code === 0)
      .reduce((sum, invoice) => sum + invoice.amount, 0);
    const processing = invoices
      .filter(inv => inv.status.code === 1)
      .reduce((sum, invoice) => sum + invoice.amount, 0);
    const disbursed = invoices
      .filter(inv => inv.status.code === 2)
      .reduce((sum, invoice) => sum + invoice.amount, 0);
    
    return { total, requested, processing, disbursed };
  };

  const totals = calculateTotals();

  return (
    <div className="backoffice-invoices-container">
      <Row className="mb-3">
        <Col>
          <h4>Invoice Management</h4>
          <p className="text-muted">Process and manage Agent invoices</p>
        </Col>
      </Row>
      
      {/* Status summary cards */}
      <Row className="mb-4">
        <Col md={6} lg={3}>
          <Card className="shadow-sm h-100 bg-primary bg-opacity-10">
            <Card.Body className="d-flex">
              <div className="d-flex align-items-center justify-content-center me-3" style={{ background: 'rgba(99, 102, 241, 0.2)', width: '48px', height: '48px', borderRadius: '8px' }}>
                <FaMoneyBillWave size={24} color="#6366f1" />
              </div>
              <div>
                <div className="text-muted small mb-1">Total Invoices</div>
                <h3 className="mb-0 fs-4">₹{totals.total.toLocaleString()}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6} lg={3}>
          <Card className="shadow-sm h-100 bg-info bg-opacity-10">
            <Card.Body className="d-flex">
              <div className="d-flex align-items-center justify-content-center me-3" style={{ background: 'rgba(14, 165, 233, 0.2)', width: '48px', height: '48px', borderRadius: '8px' }}>
                <FaFileInvoice size={24} color="#0ea5e9" />
              </div>
              <div>
                <div className="text-muted small mb-1">Generated</div>
                <h3 className="mb-0 fs-4">₹{totals.requested.toLocaleString()}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6} lg={3}>
          <Card className="shadow-sm h-100 bg-warning bg-opacity-10">
            <Card.Body className="d-flex">
              <div className="d-flex align-items-center justify-content-center me-3" style={{ background: 'rgba(245, 158, 11, 0.2)', width: '48px', height: '48px', borderRadius: '8px' }}>
                <FaInfoCircle size={24} color="#f59e0b" />
              </div>
              <div>
                <div className="text-muted small mb-1">Processing</div>
                <h3 className="mb-0 fs-4">₹{totals.processing.toLocaleString()}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6} lg={3}>
          <Card className="shadow-sm h-100 bg-success bg-opacity-10">
            <Card.Body className="d-flex">
              <div className="d-flex align-items-center justify-content-center me-3" style={{ background: 'rgba(16, 185, 129, 0.2)', width: '48px', height: '48px', borderRadius: '8px' }}>
                <FaCheckCircle size={24} color="#10b981" />
              </div>
              <div>
                <div className="text-muted small mb-1">Disbursed</div>
                <h3 className="mb-0 fs-4">₹{totals.disbursed.toLocaleString()}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Search and filter controls */}
      <Card className="shadow-sm mb-4">
        <Card.Body>
          <Row className="align-items-center g-3">
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search by invoice number, DSO or agent name..."
                  value={searchTerm}
                  onChange={handleSearch}
                />
              </InputGroup>
            </Col>
            
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <FaFilter />
                </InputGroup.Text>
                <Form.Select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                >
                  <option value="All">All Status</option>
                  <option value="Generated">Generated</option>
                  <option value="Processing">Processing</option>
                  <option value="Disbursed">Disbursed</option>
                  <option value="Rejected">Rejected</option>
                </Form.Select>
              </InputGroup>
            </Col>
            <Col md={2} className="text-end">
              <Button variant="outline-primary" onClick={fetchInvoices}>
                Refresh
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      {/* Invoices table */}
      <Card className="shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center p-5">
              <Spinner animation="border" />
              <p className="mt-2">Loading invoices...</p>
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center p-5">
              <FaExclamationTriangle size={24} className="text-muted mb-3" />
              <h5>No invoices found</h5>
              <p className="text-muted">Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <>
              <Table responsive hover className="mb-0">
                <thead>
                  <tr>
                    <th>Invoice No.</th>
                    <th>Agent</th>
                    <th>DSO</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Payment Status</th>
                    <th>Created Date</th>
                    <th>Payment Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(invoice => (
                    <tr key={invoice.id}>
                      <td>{invoice.invoice_no}</td>
                      <td>{invoice.agent?.name || 'N/A'}</td>
                      <td>{invoice.dso?.name || 'N/A'}</td>
                      <td>₹{invoice.amount.toLocaleString()}</td>
                      <td>{renderStatusBadge(invoice.status.code)}</td>
                      <td>{renderPaymentStatusBadge(invoice.payment_status.code)}</td>
                      <td>{new Date(invoice.created_at).toLocaleDateString()}</td>
                      <td>{invoice.payment_date ? new Date(invoice.payment_date).toLocaleDateString() : '-'}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            onClick={() => handleViewInvoice(invoice)}
                            disabled={processingAction}
                            title="View Details"
                          >
                            <FaEye />
                          </Button>
                          
                          {invoice.status.code === 0 && ( // Generated
                            <Button 
                              variant="outline-warning" 
                              size="sm" 
                              onClick={() => handleViewInvoice(invoice)}
                              disabled={processingAction}
                              title="Process Invoice"
                            >
                              Process
                            </Button>
                          )}
                          
                          {invoice.status.code === 1 && ( // Processing
                            <Button 
                              variant="outline-success" 
                              size="sm" 
                              onClick={() => handleViewInvoice(invoice)}
                              disabled={processingAction}
                              title="Disburse Invoice"
                            >
                              Disburse
                            </Button>
                          )}

                          {invoice.invoice_url && (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              as="a"
                              href={invoice.invoice_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Download Invoice PDF"
                            >
                              <FaDownload />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              
              {/* Pagination */}
              <div className="d-flex justify-content-between align-items-center p-3">
                <div>
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} invoices
                </div>
                <div>
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    disabled={pagination.page === 1} 
                    onClick={() => handlePageChange(pagination.page - 1)}
                    className="me-2"
                  >
                    Previous
                  </Button>
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    disabled={pagination.page * pagination.limit >= pagination.total}
                    onClick={() => handlePageChange(pagination.page + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
      
      {/* Invoice Detail Modal */}
      <Modal 
        show={showInvoiceModal} 
        onHide={() => setShowInvoiceModal(false)} 
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Invoice Details - {selectedInvoice?.invoice_no}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedInvoice && (
            <Tabs defaultActiveKey="details" className="mb-3">
              <Tab eventKey="details" title="Invoice Information">
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="text-muted small">Invoice Number</label>
                      <div className="fw-medium">{selectedInvoice.invoice_no}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="text-muted small">Agent Name</label>
                      <div className="fw-medium">{selectedInvoice.agent?.name || 'N/A'}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="text-muted small">DSO Name</label>
                      <div className="fw-medium">{selectedInvoice.dso?.name || 'N/A'}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="text-muted small">Case Count</label>
                      <div className="fw-medium">{selectedInvoice.case_count}</div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <label className="text-muted small">Amount</label>
                      <div className="fw-medium">₹{selectedInvoice.amount.toLocaleString()}</div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <label className="text-muted small">GST</label>
                      <div className="fw-medium">₹{(selectedInvoice.gst_amount || 0).toLocaleString()}</div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <label className="text-muted small">Total Amount</label>
                      <div className="fw-medium">₹{selectedInvoice.total_amount.toLocaleString()}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="text-muted small">Status</label>
                      <div>{renderStatusBadge(selectedInvoice.invoice_status)}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="text-muted small">Payment Status</label>
                      <div>{renderPaymentStatusBadge(selectedInvoice.payment_status)}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="text-muted small">Created At</label>
                      <div className="fw-medium">{new Date(selectedInvoice.created_at).toLocaleString()}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="text-muted small">Payment Date</label>
                      <div className="fw-medium">{selectedInvoice.payment_date ? new Date(selectedInvoice.payment_date).toLocaleString() : '-'}</div>
                    </div>
                  </Col>
                </Row>
                
                {selectedInvoice.invoice_url && (
                  <div className="mt-3">
                    <Button 
                      variant="outline-primary"
                      href={selectedInvoice.invoice_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaDownload className="me-2" />
                      Download Invoice PDF
                    </Button>
                  </div>
                )}
              </Tab>
              
              <Tab eventKey="cases" title={`Related Cases (${selectedInvoiceCases.length})`}>
                {selectedInvoiceCases.length === 0 ? (
                  <Alert variant="info">No cases found associated with this invoice.</Alert>
                ) : (
                  <Table responsive hover className="mb-0">
                    <thead>
                      <tr>
                        <th>Case ID</th>
                        <th>Customer Name</th>
                        <th>Product</th>
                        <th>Loan Amount</th>
                        <th>Status</th>
                        <th>Payout Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInvoiceCases.map(caseItem => (
                        <tr key={caseItem.id}>
                          <td>{caseItem.application_no || caseItem.id}</td>
                          <td>{caseItem.customer?.name || 'Unknown'}</td>
                          <td>{caseItem.product?.name || 'Unknown'}</td>
                          <td>₹{caseItem.loan_amount?.toLocaleString() || '0'}</td>
                          <td>
                            <Badge bg={caseItem.status?.code >= 12 ? 'success' : 'primary'}>
                              {caseItem.status?.name || 'Unknown'}
                            </Badge>
                          </td>
                          <td>₹{caseItem.payout_amount?.toLocaleString() || '0'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Tab>
              
              <Tab eventKey="actions" title="Actions">
                <div className="mb-4">
                  <h6 className="mb-3">Update Invoice Status</h6>
                  <div className="d-flex flex-wrap gap-2">
                    <Button 
                      variant={selectedInvoice.invoice_status === 0 ? "info" : "outline-info"} 
                      onClick={() => handleStatusChange(selectedInvoice.id, 0)}
                      disabled={processingAction}
                    >
                      Generated
                    </Button>
                    <Button 
                      variant={selectedInvoice.invoice_status === 1 ? "warning" : "outline-warning"}
                      onClick={() => handleStatusChange(selectedInvoice.id, 1)}
                      disabled={processingAction}
                    >
                      Processing
                    </Button>
                    <Button 
                      variant={selectedInvoice.invoice_status === 2 ? "success" : "outline-success"} 
                      onClick={() => handleStatusChange(selectedInvoice.id, 2)}
                      disabled={processingAction}
                    >
                      Disburse
                    </Button>
                    <Button 
                      variant={selectedInvoice.invoice_status === 3 ? "danger" : "outline-danger"} 
                      onClick={() => handleStatusChange(selectedInvoice.id, 3)}
                      disabled={processingAction}
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              </Tab>
            </Tabs>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowInvoiceModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Toast notifications */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 1060 }}>
        <Toast 
          show={showToast} 
          onClose={() => setShowToast(false)} 
          delay={3000} 
          autohide
          bg={toastVariant}
          text={toastVariant === 'warning' ? 'dark' : 'white'}
        >
          <Toast.Header closeButton>
            <strong className="me-auto">
              {toastVariant === 'success' ? 'Success' : 
               toastVariant === 'danger' ? 'Error' : 'Notification'}
            </strong>
          </Toast.Header>
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default BackofficeInvoices;